import Swup from "swup";
import { scrollService } from "./scrollService";
import { SiteHeader } from "../blocks.common/SiteHeader";

type SwupPage = {
	originalContent?: string;
	pageClass?: string;
};

const TRANSPARENT_HEADER_SELECTOR =
	".umb-block-grid__layout-item:first-of-type .transparent-header";
const TRANSPARENT_HEADER_DARK_SELECTOR =
	".umb-block-grid__layout-item:first-of-type .transparent-header-dark";

class SwupService {
	siteHeader: SiteHeader;
	swup: Swup;

	constructor() {
		this.siteHeader = document.querySelector(
			`header[is="${SiteHeader.NAME}"]`,
		) as SiteHeader;

		this.swup = new Swup({
			animationSelector: '[class*="swup-"]',
			containers: ["#swup"],
		});

		this.syncTransparentHeaderState(document);

		this.swup.on("clickLink", () => {
			this.siteHeader?.closeMenu();
		});

		this.swup.on("samePageWithHash", () => {
			const hash = document.location.hash;
			if (!hash) {
				return;
			}
			scrollService.lenis.scrollTo(hash);
		});

		this.swup.on("samePage", () => {
			scrollService.lenis.scrollTo(0);
		});

		this.swup.on("willReplaceContent", () => {
			this.syncDocumentFromTransition();
		});

		this.swup.on("contentReplaced", () => {
			const swupRoot = document.querySelector<HTMLElement>("#swup");
			void swupRoot?.offsetHeight;

			if (location.hash) {
				location.href = location.hash;
			} else {
				window.scrollTo(0, 0);
			}

			scrollService.reinit();
			this.siteHeader?.refreshInertElements();
		});
	}

	private syncDocumentFromTransition() {
		const url = this.swup.transition.to;
		if (!url) {
			return;
		}

		this.syncDocumentFromPage(this.swup.cache.getPage(url));
	}

	private syncDocumentFromPage(page: SwupPage | null | undefined) {
		if (!page?.originalContent) {
			return;
		}

		const doc = new DOMParser().parseFromString(
			page.originalContent,
			"text/html",
		);
		const title = document.head.querySelector("title");
		const metaSelector = `meta[name], meta[property], link[rel="canonical"]`;
		const oldMeta = document.head.querySelectorAll(metaSelector);
		const newMeta = doc.querySelectorAll(metaSelector);
		oldMeta.forEach((tag) => tag.remove());
		title?.after(...newMeta);

		document.body.className = page.pageClass ?? doc.body.className;
		document.documentElement.lang = doc.documentElement.lang;
		this.syncTransparentHeaderState(doc);

		if (!this.swup.scrollToElement) {
			document.documentElement.classList.remove("scrolled");
		}
	}

	private syncTransparentHeaderState(root: ParentNode) {
		const hasTransparentHeader = !!root.querySelector(
			TRANSPARENT_HEADER_SELECTOR,
		);
		const hasTransparentHeaderDark = !!root.querySelector(
			TRANSPARENT_HEADER_DARK_SELECTOR,
		);

		document.documentElement.classList.toggle(
			"has-transparent-header",
			hasTransparentHeader,
		);
		document.documentElement.classList.toggle(
			"has-transparent-header-dark",
			hasTransparentHeaderDark,
		);
	}
}

export const swupService = new SwupService();
